import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { apiError, getResearch, postResearch } from '../../lib/research';
import { assertScannerRun } from './markets';
import { scannerPath } from '../../lib/marketRoutes';

const pause = (ms, signal) => new Promise(resolve => {
  const finish = () => { clearTimeout(timer); signal.removeEventListener('abort', finish); resolve(); };
  const timer = setTimeout(finish, ms);
  signal.addEventListener('abort', finish, { once: true });
  if (signal.aborted) finish();
});

export const useScanner = (scope = 'crypto') => {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const [config, setConfig] = useState(null), [runs, setRuns] = useState([]), [run, setRun] = useState(null);
  const [busy, setBusy] = useState(false), [loading, setLoading] = useState(true), [error, setError] = useState('');
  const [configLoading, setConfigLoading] = useState(true);
  const [progress, setProgress] = useState(null);
  const [market, setMarketState] = useState(scope), [stopping, setStopping] = useState(false);
  const controller = useRef(null), activeId = useRef(null), alive = useRef(true), selection = useRef(0);
  const stoppingRef = useRef(false);
  const historyRequest = useRef(0);
  const displayedId = useRef(null), openingId = useRef(null), queryId = useRef(params.get('run'));
  const requestedId = params.get('run');
  queryId.current = requestedId;
  const setMarket = value => {
    if (!controller.current && !stoppingRef.current && !loading && run?.status !== 'running') setMarketState(value);
  };
  const history = useCallback(async () => {
    const ticket = ++historyRequest.current;
    const { data } = await getResearch('/scanner/runs', { params: { workspace: scope === 'stock' ? 'stocks' : 'crypto' } });
    if (alive.current && ticket === historyRequest.current) setRuns(data);
    return data;
  }, [scope]);
  const open = async id => {
    if (controller.current || stoppingRef.current) return;
    const ticket = ++selection.current;
    openingId.current = id;
    setLoading(true); setError('');
    try {
      const { data } = await getResearch(`/scanner/runs/${id}`);
      assertScannerRun(data, id);
      if ((scope === 'crypto' && data.market !== 'crypto') || (scope === 'stock' && data.market === 'crypto')) {
        if (alive.current && ticket === selection.current) navigate(scannerPath(data.market, id), { replace: true });
        return;
      }
      if (alive.current && ticket === selection.current) { displayedId.current = id; setRun(data); setMarketState(data.market); setProgress(null); setParams({ run: id }, { replace: true }); }
    } catch (err) { if (alive.current && ticket === selection.current) setError(apiError(err)); }
    finally { if (alive.current && ticket === selection.current) { openingId.current = null; setLoading(false); } }
  };
  const load = async () => {
    if (controller.current || stoppingRef.current) return;
    setConfigLoading(true); setError('');
    try {
      const [{ data }, saved] = await Promise.all([getResearch('/scanner/config'), history()]);
      if (!alive.current) return;
      setConfig(data);
      const id = queryId.current || saved[0]?.id;
      if (id && openingId.current !== id && displayedId.current !== id) await open(id);
      else if (!id) setLoading(false);
    } catch (err) { if (alive.current) setError(apiError(err)); }
    finally { if (alive.current) { setConfigLoading(false); if (!openingId.current) setLoading(false); } }
  };
  useEffect(() => {
    alive.current = true; load();
    return () => { alive.current = false; controller.current?.abort(); };
    // Viewing/reloading saved progress never resumes billed processing automatically.
  }, []);
  useEffect(() => {
    if (requestedId === displayedId.current && (!openingId.current || openingId.current === requestedId)) return;
    ++selection.current;
    if (stoppingRef.current) return;
    // Browser navigation pauses this client; it never cancels the saved run.
    controller.current?.abort(); controller.current = null; activeId.current = null;
    setBusy(false); setProgress(null);
    if (requestedId) open(requestedId);
    else {
      displayedId.current = null; setRun(null); setLoading(false);
      if (runs[0]?.id) open(runs[0].id);
    }
  }, [requestedId, stopping]);
  const drive = async (market, resumeId = null) => {
    if (controller.current || stoppingRef.current || loading || configLoading) return;
    const ticket = ++selection.current;
    const abort = new AbortController(); controller.current = abort; activeId.current = resumeId;
    setMarketState(market); setBusy(true); setError(''); setProgress({ stage: 'starting' });
    try {
      let response = resumeId ? await getResearch(`/scanner/runs/${resumeId}`, { signal: abort.signal })
        : await postResearch('/scanner/scan', { market }, { signal: abort.signal });
      assertScannerRun(response.data, resumeId, market);
      activeId.current = resumeId || response.data.id;
      while (!abort.signal.aborted) {
        const current = response.data;
        assertScannerRun(current, activeId.current, market);
        if (alive.current && ticket === selection.current) { displayedId.current = current.id; setRun(current); setProgress(current.status === 'running' ? current : null); setParams({ run: current.id }, { replace: true }); }
        if (current.status !== 'running') break;
        if (current.retry_after_seconds > 0) await pause(Math.min(current.retry_after_seconds, 120) * 1000, abort.signal);
        if (abort.signal.aborted) break;
        try {
          response = await postResearch(`/scanner/runs/${current.id}/advance`, {}, { timeout: 50000, signal: abort.signal });
        } catch (err) {
          if (err.response?.status !== 409) throw err;
          await new Promise(resolve => setTimeout(resolve, 1200));
          if (abort.signal.aborted) break;
          response = await getResearch(`/scanner/runs/${current.id}`, { signal: abort.signal });
        }
      }
    } catch (err) {
      if (alive.current && ticket === selection.current && err.name !== 'CanceledError' && err.name !== 'AbortError') {
        setError(`${apiError(err)} Saved progress can be resumed without starting over.`);
      }
    } finally {
      if (controller.current === abort && !stoppingRef.current) {
        controller.current = null; activeId.current = null;
        if (alive.current && ticket === selection.current) { setBusy(false); setProgress(null); history().catch(() => { if (alive.current && ticket === selection.current) setError('Saved runs could not be refreshed. Your current progress remains visible.'); }); }
      }
    }
  };
  const stop = async () => {
    if (stoppingRef.current) return;
    const id = activeId.current || (run?.status === 'running' ? run.id : null);
    if (!id) return;
    stoppingRef.current = true; setStopping(true); const ticket = ++selection.current;
    const abort = controller.current;
    abort?.abort();
    try {
      const { data } = await postResearch(`/scanner/runs/${id}/cancel`, {});
      assertScannerRun(data, id, run?.id === id ? run.market : market);
      if (alive.current && ticket === selection.current) { setRun(data); setMarketState(data.market); setProgress(null); await history(); }
    } catch (err) { if (alive.current && ticket === selection.current) setError(apiError(err)); }
    finally {
      if (controller.current === abort) controller.current = null;
      if (ticket !== selection.current) displayedId.current = null;
      activeId.current = null; stoppingRef.current = false;
      if (alive.current) { setStopping(false); setBusy(false); setProgress(null); }
    }
  };
  return { config, runs, run, busy, loading: loading || configLoading, error, progress, market, setMarket, stopping, scan: () => drive(scope),
    resume: () => drive(run?.market, run?.id), open, reload: load, stop };
};